import { AxiosError, HttpStatusCode } from 'axios'

import { useCreateInvite } from '@/api/models/users/hooks/useCreateInvite'
import { CreateInviteDTO } from '@/api/models/users/lib/invites.types'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { ErrorCard } from '@/components/pages/plugs'
import { PERMISSIONS_EXPIRES_AT_LABEL } from '@/components/shared/datepicker/model/permissionsExpiresAt'
import { AppInput } from '@/components/shared/input'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'
import { formatPhoneNumber } from '@/utils/number'

import { InviteCreationStage } from '../../../lib/types'
import { formatPermissionsExpiresAt } from '../../../lib/utils'
import { useUserProfileStore } from '../../../model/store'
import { FixedBottomDrawerButton } from '../../FixedBottomDrawerButton'
import {
  PermissionsBlock,
  PermissionsBlockProject
} from '../../PermissionsBlock'
import {
  InviteCreationFormData,
  SelectedPersonalAccount
} from '../model/schemas'

type Props = {
  inviteData: InviteCreationFormData
}

type GroupedProject = {
  projectId: string
  projectName: string
  personalAccounts: SelectedPersonalAccount[]
}

const groupAccountsByProject = (
  accounts: SelectedPersonalAccount[]
): GroupedProject[] => {
  const result: GroupedProject[] = []

  accounts.forEach(account => {
    const existingProject = result.find(
      project => project.projectId === account.projectId
    )

    if (existingProject) {
      existingProject.personalAccounts.push(account)
    } else {
      result.push({
        projectId: account.projectId,
        projectName: account.projectName,
        personalAccounts: [account]
      })
    }
  })

  return result
}

export const InviteCreationConfirmationScreen = ({ inviteData }: Props) => {
  const { reachGoal } = useYandexMetrika()

  const { setInviteCreationStage, setSentInvitePermissionsExpiresAt } =
    useUserProfileStore()
  const { mutate: createInvite, isPending, isError, error } = useCreateInvite()

  const isConflictError =
    (error as AxiosError)?.response?.status === HttpStatusCode.Conflict

  const groupedProjects = groupAccountsByProject(inviteData.personalAccounts)

  const accessProjects: PermissionsBlockProject[] = groupedProjects.map(
    ({ projectId, projectName, personalAccounts }) => ({
      id: projectId,
      name: projectName,
      rows: personalAccounts.map(
        ({ personalAccountId, premises, permissions, autoPermissions }) => ({
          id: personalAccountId,
          premise: premises.map(({ premiseName }) => premiseName).join(', '),
          permissions: [...permissions, ...autoPermissions]
            .map(({ name }) => name)
            .join(', ')
        })
      )
    })
  )

  const onCreateInvite = () => {
    const payload: CreateInviteDTO = {
      user: {
        phone: inviteData.phone,
        customName: inviteData.customName ?? ''
      },
      permissionsExpiresAt: inviteData.permissionsExpiresAt ?? null,
      personalAccounts: inviteData.personalAccounts.map(
        ({ personalAccountId, permissions }) => ({
          personalAccountId,
          permissions: permissions.map(({ alias }) => alias)
        })
      )
    }

    createInvite(payload, {
      onSuccess: () => {
        setSentInvitePermissionsExpiresAt(payload.permissionsExpiresAt ?? null)
        setInviteCreationStage(InviteCreationStage.CreationSucceed)
        reachGoal(YandexMetrikaEvent.CONTACTS_CREATE_FINISH)
      },
      onError: error => {
        if (
          (error as AxiosError)?.response?.status === HttpStatusCode.Conflict
        ) {
          setInviteCreationStage(InviteCreationStage.CreationFailed)
        }
      }
    })
  }

  if (isError && !isConflictError) {
    return (
      <div className="relative flex grow flex-col overflow-hidden rounded-16 p-32 pb-0 sm:p-16 xxs:h-auto">
        <div className="mb-24 flex h-full justify-center">
          <ErrorCard />
        </div>
      </div>
    )
  }

  return (
    <div className="mb-100 flex flex-col gap-20 overflow-y-auto px-40 pb-40 lg:px-24 lg:pb-24 sm:px-16 sm:pb-20">
      <p className="font-semibold text-20">
        Вы добавили новый контакт, ему будет отправлено уведомление на телефон{' '}
        <span className="text-bronze-40">
          {formatPhoneNumber(inviteData.phone ?? '')}
        </span>
        {'. '}В уведомлении будет вся необходимая информация для авторизации в
        приложении.
      </p>

      {inviteData?.customName && (
        <AppInput
          dataTestId="invite-confirmation-description-input-id"
          className="!h-56 border border-grey-20 bg-white xxs:!h-48"
          inputClassName="opacity-50"
          placeholder="Описание контакта"
          disabled
          defaultValue={inviteData.customName}
        />
      )}

      <AppInput
        dataTestId="invite-confirmation-expires-at-input-id"
        className="!h-56 border border-grey-20 bg-white xxs:!h-48"
        inputClassName="opacity-50"
        placeholder={PERMISSIONS_EXPIRES_AT_LABEL}
        disabled
        defaultValue={formatPermissionsExpiresAt(
          inviteData.permissionsExpiresAt
        )}
      />

      <PermissionsBlock projects={accessProjects} />
      <FixedBottomDrawerButton
        onClick={onCreateInvite}
        text="Отправить приглашение"
        loading={isPending}
      />
    </div>
  )
}
